import React, { useState, useEffect } from 'react';
import {
  Shield,
  RefreshCw,
  Phone,
  Building2,
  Link,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  CreditCard,
  ExternalLink,
  Search,
  X,
  Radio,
  Wifi,
  Key,
} from 'lucide-react';
import { whatsappService } from 'api/services/whatsapp.service';
import { WABARegistration, CreditLineLinkStatus, PhoneRegistrationStatus } from 'types';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';

const WABARegistrations: React.FC = () => {
  const { currentOrganization } = useAuth();

  // Check admin access (master_admin or super_admin only)
  const isAdmin =
    currentOrganization?.role === 'master_admin' ||
    currentOrganization?.role === 'super_admin';

  // State
  const [registrations, setRegistrations] = useState<WABARegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Link result modal
  const [showLinkResultModal, setShowLinkResultModal] = useState(false);
  const [linkResult, setLinkResult] = useState<{
    success: boolean;
    message: string;
    data?: any;
  } | null>(null);

  // Phone Registration state
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registeringPhone, setRegisteringPhone] = useState<{
    configId: string;
    phoneNumber: string;
    phoneNumberId: string;
    organizationId: string;
  } | null>(null);
  const [registerPin, setRegisterPin] = useState('');
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [showRegisterResultModal, setShowRegisterResultModal] = useState(false);
  const [registerResult, setRegisterResult] = useState<{
    success: boolean;
    message: string;
    data?: any;
  } | null>(null);

  // Webhook Subscription state
  const [subscribingWabaId, setSubscribingWabaId] = useState<string | null>(null);
  const [showWebhookResultModal, setShowWebhookResultModal] = useState(false);
  const [webhookResult, setWebhookResult] = useState<{
    success: boolean;
    message: string;
    data?: any;
  } | null>(null);

  useEffect(() => {
    if (isAdmin) {
      fetchRegistrations();
    }
  }, [isAdmin]);

  const fetchRegistrations = async () => {
    setLoading(true);
    setError(null);

    const result = await whatsappService.getWABARegistrations();

    if (result.success && result.data) {
      setRegistrations(result.data);
    } else {
      setError(result.error || 'Failed to fetch registrations');
    }

    setLoading(false);
  };

  const handleLinkCreditLine = async (registration: WABARegistration) => {
    if (!confirm(`Are you sure you want to link credit line for ${registration.businessOrganizationName}?`)) {
      return;
    }

    setLinkingId(registration.registrationId);

    const result = await whatsappService.linkCreditLineByRegistration(registration.registrationId);

    setLinkResult({
      success: result.success,
      message: result.success
        ? result.message || 'Credit line linked successfully'
        : result.error || 'Failed to link credit line',
      data: result.data,
    });
    setShowLinkResultModal(true);

    if (result.success) {
      await fetchRegistrations();
    }

    setLinkingId(null);
  };

  // Handle phone registration - open modal
  const handleOpenRegisterModal = (
    registration: WABARegistration,
    configId: string,
    phoneNumberId: string,
    phoneNumber: string
  ) => {
    setRegisteringPhone({
      configId,
      phoneNumber,
      phoneNumberId,
      organizationId: registration.businessOrganizationId,
    });
    setRegisterPin('');
    setShowRegisterModal(true);
  };

  // Handle phone registration - submit
  const handleRegisterPhone = async () => {
    if (!registeringPhone || registerPin.length !== 6) return;

    setRegistrationLoading(true);

    const result = await whatsappService.registerPhoneNumberByConfigId(registeringPhone.configId, registerPin);

    setRegisterResult({
      success: result.success,
      message: result.success
        ? result.message || 'Phone number registered with Meta successfully'
        : result.error || 'Failed to register phone number',
      data: result.data,
    });

    setShowRegisterModal(false);
    setShowRegisterResultModal(true);
    setRegistrationLoading(false);

    if (result.success) {
      // Refresh registrations to get updated status
      await fetchRegistrations();
    }
  };

  // Handle webhook subscription
  const handleSubscribeWebhooks = async (wabaId: string) => {
    if (!confirm(`Are you sure you want to subscribe webhooks for WABA ${wabaId}?`)) {
      return;
    }

    setSubscribingWabaId(wabaId);

    const result = await whatsappService.subscribeWebhooksByWabaId(wabaId);

    setWebhookResult({
      success: result.success,
      message: result.success
        ? result.message || 'Webhooks subscribed successfully'
        : result.error || 'Failed to subscribe webhooks',
      data: result.data,
    });
    setShowWebhookResultModal(true);

    setSubscribingWabaId(null);
  };

  // Get registration status badge for phone numbers
  const getRegistrationBadge = (status?: PhoneRegistrationStatus) => {
    if (!status || status === 'NOT_REGISTERED') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          Not Registered
        </span>
      );
    }
    if (status === 'REGISTERED') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Registered
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-400 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        Failed
      </span>
    );
  };

  // Get credit line status badge for phone numbers
  const getPhoneCreditLineBadge = (status?: CreditLineLinkStatus) => {
    if (!status || status === 'NOT_LINKED') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          Not Linked
        </span>
      );
    }
    if (status === 'LINKED') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Linked
        </span>
      );
    }
    if (status === 'PENDING') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-400 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Pending
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-400 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        Failed
      </span>
    );
  };

  const getStatusBadge = (status: CreditLineLinkStatus) => {
    const styles: Record<CreditLineLinkStatus, { bg: string; icon: React.ReactNode; text: string }> = {
      LINKED: {
        bg: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400',
        icon: <CheckCircle className="w-4 h-4" />,
        text: 'Linked',
      },
      NOT_LINKED: {
        bg: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
        icon: <XCircle className="w-4 h-4" />,
        text: 'Not Linked',
      },
      PENDING: {
        bg: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-400',
        icon: <Clock className="w-4 h-4" />,
        text: 'Pending',
      },
      FAILED: {
        bg: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-400',
        icon: <AlertCircle className="w-4 h-4" />,
        text: 'Failed',
      },
    };

    const style = styles[status] || styles.NOT_LINKED;

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${style.bg}`}>
        {style.icon}
        {style.text}
      </span>
    );
  };

  // Filter registrations based on search term
  const filteredRegistrations = registrations.filter((reg) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      reg.businessOrganizationName?.toLowerCase().includes(term) ||
      reg.businessId?.toLowerCase().includes(term) ||
      reg.wabaId?.toLowerCase().includes(term) ||
      reg.primaryPhoneNumber?.toLowerCase().includes(term)
    );
  });

  // Stats
  const totalRegistrations = registrations.length;
  const linkedCount = registrations.filter((r) => r.creditLineLinkStatus === 'LINKED').length;
  const notLinkedCount = registrations.filter((r) => r.creditLineLinkStatus === 'NOT_LINKED').length;
  const pendingCount = registrations.filter((r) => r.creditLineLinkStatus === 'PENDING').length;

  // Access denied
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Shield className="w-16 h-16 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">Access Denied</h2>
        <p className="text-gray-600 dark:text-gray-400">
          You don't have permission to access this page. Admin access required.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <CreditCard className="w-7 h-7" />
            WABA Registrations
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage WhatsApp Business Account registrations and credit line linking
          </p>
        </div>
        <button
          onClick={fetchRegistrations}
          disabled={loading}
          className="tl-btn tl-btn-primary disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex items-center gap-4">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
            <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalRegistrations}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex items-center gap-4">
          <div className="p-3 bg-green-100 dark:bg-green-900/40 rounded-lg">
            <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{linkedCount}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Linked</p>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex items-center gap-4">
          <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <XCircle className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{notLinkedCount}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Not Linked</p>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex items-center gap-4">
          <div className="p-3 bg-yellow-100 dark:bg-yellow-900/40 rounded-lg">
            <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{pendingCount}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Pending</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by organization, business ID, WABA ID, or phone number..."
            className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-300">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Registrations List */}
      {filteredRegistrations.length === 0 && !loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
          <Building2 className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
            {searchTerm ? 'No matching registrations' : 'No WABA Registrations'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {searchTerm
              ? 'Try adjusting your search terms'
              : 'WhatsApp Business Account registrations will appear here once businesses complete signup.'}
          </p>
        </div>
      )}

      {filteredRegistrations.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
            Registrations ({filteredRegistrations.length})
          </h2>

          {filteredRegistrations.map((registration) => (
            <div
              key={registration.registrationId}
              className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
              {/* Card Header */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
                onClick={() =>
                  setExpandedId(expandedId === registration.registrationId ? null : registration.registrationId)
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
                      <Building2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {registration.businessOrganizationName}
                        </h3>
                        {getStatusBadge(registration.creditLineLinkStatus)}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {registration.totalPhoneNumbers} number(s)
                        </span>
                        <span>WABA: {registration.wabaId}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {registration.creditLineLinkStatus === 'NOT_LINKED' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLinkCreditLine(registration);
                        }}
                        disabled={linkingId === registration.registrationId}
                        className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1 transition"
                      >
                        {linkingId === registration.registrationId ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Linking...
                          </>
                        ) : (
                          <>
                            <Link className="w-4 h-4" />
                            Link Credit Line
                          </>
                        )}
                      </button>
                    )}
                    {expandedId === registration.registrationId ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === registration.registrationId && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-700/30">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Registration ID
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1 font-mono">
                        {registration.registrationId}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Business ID
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                        {registration.businessId}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Organization ID
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1 font-mono text-xs">
                        {registration.businessOrganizationId}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        WABA ID
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                        {registration.wabaId}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Primary Phone
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {registration.primaryPhoneNumber || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Active Numbers
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                        {registration.activePhoneNumbers} / {registration.totalPhoneNumbers}
                      </p>
                    </div>
                    {registration.providerOrganizationName && (
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Provider
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                          {registration.providerOrganizationName}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Credit Line Status
                      </p>
                      <div className="mt-1">{getStatusBadge(registration.creditLineLinkStatus)}</div>
                    </div>
                    {registration.creditLineLinkedAt && (
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Linked At
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                          {new Date(registration.creditLineLinkedAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                    {registration.completedAt && (
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Registration Completed
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                          {new Date(registration.completedAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Phone Numbers Table */}
                  {registration.phoneNumbers && registration.phoneNumbers.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Phone Numbers
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-100 dark:bg-gray-700">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Phone Number
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Display Name
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Status
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Registration
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Credit Line
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Primary
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {registration.phoneNumbers.map((phone) => (
                              <tr key={phone.id}>
                                <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                  {phone.phoneNumber}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                                  {phone.displayName || '-'}
                                </td>
                                <td className="px-3 py-2">
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-xs ${
                                      phone.active
                                        ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                    }`}
                                  >
                                    {phone.active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  {getRegistrationBadge(phone.registrationStatus)}
                                </td>
                                <td className="px-3 py-2">
                                  {getPhoneCreditLineBadge(phone.creditLineStatus)}
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  {phone.isPrimary && (
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {(!phone.registrationStatus || phone.registrationStatus !== 'REGISTERED') && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenRegisterModal(registration, phone.id, phone.phoneNumberId, phone.phoneNumber);
                                      }}
                                      className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 flex items-center gap-1 transition"
                                    >
                                      <Radio className="w-3 h-3" />
                                      Register
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 flex flex-wrap gap-3">
                    {registration.creditLineLinkStatus === 'NOT_LINKED' && (
                      <button
                        onClick={() => handleLinkCreditLine(registration)}
                        disabled={linkingId === registration.registrationId}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 transition"
                      >
                        {linkingId === registration.registrationId ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Linking...
                          </>
                        ) : (
                          <>
                            <Link className="w-4 h-4" />
                            Link Credit Line
                          </>
                        )}
                      </button>
                    )}
                    {registration.creditLineLinkStatus === 'FAILED' && (
                      <button
                        onClick={() => handleLinkCreditLine(registration)}
                        disabled={linkingId === registration.registrationId}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 flex items-center gap-2 transition"
                      >
                        {linkingId === registration.registrationId ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Retrying...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4" />
                            Retry Link
                          </>
                        )}
                      </button>
                    )}
                    {/* Subscribe Webhooks Button */}
                    <button
                      onClick={() => handleSubscribeWebhooks(registration.wabaId)}
                      disabled={subscribingWabaId === registration.wabaId}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 transition"
                    >
                      {subscribingWabaId === registration.wabaId ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Subscribing...
                        </>
                      ) : (
                        <>
                          <Wifi className="w-4 h-4" />
                          Subscribe Webhooks
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Link Result Modal */}
      {showLinkResultModal && linkResult && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowLinkResultModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                {linkResult.success ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-500" />
                )}
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {linkResult.success ? 'Credit Line Linked' : 'Link Failed'}
                </h2>
              </div>
              <button
                onClick={() => setShowLinkResultModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <p
                className={`text-sm ${
                  linkResult.success
                    ? 'text-green-800 dark:text-green-300'
                    : 'text-red-800 dark:text-red-300'
                }`}
              >
                {linkResult.message}
              </p>

              {linkResult.data && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Response Details</p>
                  <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-x-auto">
                    {JSON.stringify(linkResult.data, null, 2)}
                  </pre>
                </div>
              )}

              <button
                onClick={() => setShowLinkResultModal(false)}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registration PIN Modal */}
      {showRegisterModal && registeringPhone && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowRegisterModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <Key className="w-6 h-6 text-blue-500" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Register Phone Number
                </h2>
              </div>
              <button
                onClick={() => setShowRegisterModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  Registering: <span className="font-semibold">{registeringPhone.phoneNumber}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Enter 6-digit PIN
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  This PIN will be used for two-factor authentication with Meta.
                </p>
                <input
                  type="text"
                  value={registerPin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setRegisterPin(value);
                  }}
                  placeholder="000000"
                  className="w-full px-4 py-3 text-center text-2xl tracking-[0.5em] font-mono border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  maxLength={6}
                  autoFocus
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                  {registerPin.length}/6 digits entered
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowRegisterModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRegisterPhone}
                  disabled={registerPin.length !== 6 || registrationLoading}
                  className="tl-btn tl-btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {registrationLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Registering...
                    </>
                  ) : (
                    <>
                      <Radio className="w-4 h-4" />
                      Register
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Registration Result Modal */}
      {showRegisterResultModal && registerResult && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowRegisterResultModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                {registerResult.success ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-500" />
                )}
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {registerResult.success ? 'Registration Successful' : 'Registration Failed'}
                </h2>
              </div>
              <button
                onClick={() => setShowRegisterResultModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <p
                className={`text-sm ${
                  registerResult.success
                    ? 'text-green-800 dark:text-green-300'
                    : 'text-red-800 dark:text-red-300'
                }`}
              >
                {registerResult.message}
              </p>

              {registerResult.data && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Response Details</p>
                  <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-x-auto">
                    {JSON.stringify(registerResult.data, null, 2)}
                  </pre>
                </div>
              )}

              <button
                onClick={() => setShowRegisterResultModal(false)}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Webhook Subscription Result Modal */}
      {showWebhookResultModal && webhookResult && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowWebhookResultModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                {webhookResult.success ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-500" />
                )}
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {webhookResult.success ? 'Webhooks Subscribed' : 'Subscription Failed'}
                </h2>
              </div>
              <button
                onClick={() => setShowWebhookResultModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <p
                className={`text-sm ${
                  webhookResult.success
                    ? 'text-green-800 dark:text-green-300'
                    : 'text-red-800 dark:text-red-300'
                }`}
              >
                {webhookResult.message}
              </p>

              {webhookResult.data && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Response Details</p>
                  <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-x-auto">
                    {JSON.stringify(webhookResult.data, null, 2)}
                  </pre>
                </div>
              )}

              <button
                onClick={() => setShowWebhookResultModal(false)}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WABARegistrations;
